import { useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import ReactTooltip from 'react-tooltip';
import { useTranslation } from 'react-i18next';

import fnolServices from '../../../services/fnolServices';
import { useFnol } from '../../../context';
import { formatDate } from '../../../utils';

import {
  AdjustableTextarea,
  HelperIcon,
  InputContainer,
  NumberInputField,
  RadioButton,
} from '../../../components';

const QuestionNode = ({ id, contents, isMandatory, onChange }) => {
  const { fnolTemplate, setFnolTemplate } = useFnol();
  const { t } = useTranslation();

  const nodeContents =
    contents.tag === 'QuestionNode'
      ? { ...contents.question }
      : { ...contents.answeredQuestion };

  if (contents.tag === 'AnsweredNode') {
    nodeContents.answerType = contents.answeredQuestion.answer.tag;
    nodeContents.answer = contents.answeredQuestion.answer.contents;
    nodeContents.isAnswered = true;
    nodeContents.questionTree = contents.listOfQuestionTreeOut;
  } else {
    nodeContents.answer = '';
    nodeContents.isAnswered = false;
  }

  const [answer, setAnswer] = useState(nodeContents.answer);

  const handleChange = (e) => {
    const { value } = e.target;
    onChange(id, value, nodeContents.answerType);
    setAnswer(value);
  };

  const setFnolVar = async (answer) =>
    await fnolServices.setVar(
      fnolTemplate.fnolKey,
      nodeContents.key,
      nodeContents.answerType,
      answer
    );

  const handleTextInputBlur = async () => {
    const response = await setFnolVar(answer);
    setFnolTemplate(response.data);
  };

  const handleIntegerNumberInputBlur = async () => {
    const response = await setFnolVar(Math.round(Number(answer)));
    setAnswer(Math.round(Number(answer)).toString());
    setFnolTemplate(response.data);
  };

  const handleDoubleNumberInputBlur = async () => {
    const response = await setFnolVar(Number(answer));
    setAnswer(Number(answer));
    setFnolTemplate(response.data);
  };

  const handleRadioInputChange = async (value) => {
    setAnswer(value);
    onChange(id, value);
    const response = await setFnolVar(value);
    setFnolTemplate(response.data);
  };

  const handleDateChangeRaw = (e) => {
    e.preventDefault();
  };

  const handleDateChange = async (date) => {
    onChange(id, date, nodeContents.answerType);
    setAnswer(date);
    const response = await setFnolVar(formatDate(date));
    setFnolTemplate(response.data);
  };

  const handleDateTimeChange = async (date) => {
    onChange(id, date, nodeContents.answerType);
    setAnswer(date);
    const response = await setFnolVar(date?.toISOString() ?? null);
    setFnolTemplate(response.data);
  };

  return (
    <>
      <InputContainer>
        <label>
          {nodeContents.question} {!isMandatory && t('optional')}{' '}
          {nodeContents?.description.length > 0 && (
            <>
              <HelperIcon dataFor={`tip_${nodeContents.key}`} />
              <ReactTooltip
                id={`tip_${nodeContents.key}`}
                type='dark'
                effect='solid'
                multiline={true}
                place='left'
              >
                <div style={{ maxWidth: '15rem' }}>
                  {nodeContents.description}
                </div>
              </ReactTooltip>
            </>
          )}
        </label>
        {nodeContents.answerType === 'Day' && (
          <DatePicker
            location='sv'
            dateFormat='yyyy-MM-dd'
            selected={answer ? new Date(answer) : null}
            onChange={handleDateChange}
            onChangeRaw={handleDateChangeRaw}
          />
        )}
        {nodeContents.answerType === 'Double' && (
          <NumberInputField
            value={answer || 0}
            placeholder={nodeContents.placeholder}
            onBlur={handleDoubleNumberInputBlur}
            onChange={handleChange}
          />
        )}
        {nodeContents.answerType === 'Int' && (
          <NumberInputField
            value={answer || 0}
            placeholder={nodeContents.placeholder}
            onBlur={handleIntegerNumberInputBlur}
            onChange={handleChange}
          />
        )}
        {nodeContents.answerType === 'Time' && (
          <DatePicker
            location='sv'
            dateFormat='yyyy-MM-dd HH:mm'
            showTimeSelect
            timeIntervals={5}
            timeFormat='HH:mm'
            selected={answer ? new Date(answer) : null}
            onChange={handleDateTimeChange}
            onChangeRaw={handleDateChangeRaw}
          />
        )}
        {nodeContents.answerType === 'Text' && (
          <AdjustableTextarea
            value={answer}
            placeholder={nodeContents.placeholder}
            onBlur={handleTextInputBlur}
            onChange={handleChange}
          />
        )}
        {nodeContents.answerType === 'YesNo' && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
            <RadioButton
              text={t('yes')}
              value='Yes'
              selected={answer}
              onChange={handleRadioInputChange}
            />
            <RadioButton
              text={t('no')}
              value='No'
              selected={answer}
              onChange={handleRadioInputChange}
            />
          </div>
        )}
      </InputContainer>
      {nodeContents.isAnswered &&
        nodeContents.questionTree &&
        nodeContents.questionTree.map((question) => (
          <QuestionNode
            key={`${id}_${
              question.tag === 'QuestionNode'
                ? question.question.key
                : question.answeredQuestion.key
            }`}
            id={`${id}_${
              question.tag === 'QuestionNode'
                ? question.question.key
                : question.answeredQuestion.key
            }`}
            contents={question}
            isMandatory={true}
            onChange={onChange}
          />
        ))}
    </>
  );
};

QuestionNode.propTypes = {
  contents: PropTypes.shape({
    answerType: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    question: PropTypes.string.isRequired,
  }),
  isMandatory: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default QuestionNode;
